// docs/83 PO9–PO10 — the board's pings and its right-click menu, over the board in the HUD stage.
// A ping is the chart's (`PingMark`: rings in the pinger's colour, a pin, their name), placed on its
// square by the board camera the scene publishes. The menu opens on a right-click with no hero
// selected; its Ping marks the square it opened on.
import { useEffect, useState, type CSSProperties } from 'react';
import { HotkeyKbd, PingMark } from '../components';
import { useEncounter, type Ping } from '../../core/encounter/session';
import { pingBoard, useBoardMenu } from '../../core/encounter/pings';
import { PING_DANGER, PING_MS } from '../../core/pings';
import { deviceNow } from '../../core/time';
import { useView } from '../../core/stores/view';
import { useNames } from '../../core/subscriptions/hooks';
import { DEFAULT_CELL } from '../../config';
import { playerColour, usePlayerColours } from '../screens/ChatDock';
import type { Tile } from '../../core/sim/types';

/** Where square `t`'s centre is in the stage (CSS px), from the scene's published camera. */
function useTileSpot(): (t: Tile) => { left: number; top: number } | undefined {
  const zoom = useView((s) => s.zoom);
  const scroll = useView((s) => s.scroll);
  const viewport = useView((s) => s.viewport);
  return (t) => {
    if (!viewport) return undefined;
    const px = DEFAULT_CELL * zoom;
    return { left: Math.round((t.x + 0.5) * px - scroll.x * zoom) - viewport.x, top: Math.round((t.y + 0.5) * px - scroll.y * zoom) - viewport.y };
  };
}

export function BoardPings() {
  const pings = useEncounter((s) => s.pings);
  const worldId = useEncounter((s) => s.worldId);
  const me = useEncounter((s) => s.me);
  const colours = usePlayerColours(worldId);
  const names = useNames(worldId);
  const spot = useTileSpot();
  // each ping leaves when its time is up (the store only prunes when the next one comes)
  const [, tick] = useState(0);
  useEffect(() => {
    if (!pings.length) return;
    const next = Math.min(...pings.map((p) => p.at + PING_MS)) - deviceNow();
    const t = setTimeout(() => tick((n) => n + 1), Math.max(50, next));
    return () => clearTimeout(t);
  }, [pings]);
  const live = pings.filter((p) => p.at + PING_MS > deviceNow());
  return (
    <div className="fk-hud__pings" aria-hidden="true">
      {live.map((p: Ping) => {
        const at = spot(p.tile);
        if (!at) return null;
        return <PingMark key={p.id} kind={p.kind} owner={p.owner} colour={playerColour(colours.get(p.owner))} who={p.owner === me ? 'You' : names[p.owner] ?? 'Someone'}
          style={{ left: at.left, top: at.top } as CSSProperties} testId="board-ping" />;
      })}
    </div>
  );
}

/** The right-click menu (no hero selected): Ping here (Z), Warn here (Shift+Z) — the hotkeys' current keys. */
export function BoardMenu() {
  const menu = useBoardMenu((s) => s.menu);
  const close = useBoardMenu((s) => s.close);
  useEffect(() => {
    if (!menu) return;
    const away = (e: Event) => { if (!(e.target as HTMLElement | null)?.closest?.('.fk-hud__ctx')) close(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    // a frame later, so the right-click that opened it does not close it
    const t = setTimeout(() => { window.addEventListener('pointerdown', away, true); window.addEventListener('wheel', away, true); });
    window.addEventListener('keydown', esc, true);
    return () => { clearTimeout(t); window.removeEventListener('pointerdown', away, true); window.removeEventListener('wheel', away, true); window.removeEventListener('keydown', esc, true); };
  }, [menu, close]);
  if (!menu) return null;
  const go = (kind: number) => { pingBoard(menu.tile, kind); close(); };
  const left = Math.min(menu.x, window.innerWidth - 190), top = Math.min(menu.y, window.innerHeight - 90);
  return (
    <div className="fk-hud__ctx fk-hit" role="menu" aria-label="Board" style={{ left, top }} data-testid="board-menu" onContextMenu={(e) => e.preventDefault()}>
      <button type="button" role="menuitem" className="fk-hud__ctx-item" onClick={() => go(0)} data-testid="board-menu-ping" autoFocus>
        <span className="fk-hud__ctx-pin" aria-hidden="true" />Ping<HotkeyKbd id="board.ping" />
      </button>
      <button type="button" role="menuitem" className="fk-hud__ctx-item" onClick={() => go(PING_DANGER)} data-testid="board-menu-danger">
        <span className="fk-hud__ctx-pin fk-hud__ctx-pin--danger" aria-hidden="true">!</span>Warn<HotkeyKbd id="board.warn" />
      </button>
    </div>
  );
}
